import type { ReactNode } from 'react';
import { parseRichText, type RichTextInline } from '@dermalink/shared';

function renderInline(inline: RichTextInline[], linkClassName?: string): ReactNode[] {
  return inline.map((node, i) => {
    if (node.type === 'bold') return <strong key={i}>{node.value}</strong>;
    if (node.type === 'link') {
      return (
        <a key={i} href={node.url} target="_blank" rel="noopener noreferrer" className={linkClassName}>
          {node.text}
        </a>
      );
    }
    return node.value;
  });
}

/**
 * Renders a message/snippet body as React nodes, supporting a small markdown
 * subset: **bold**, [text](url) links, bare URLs, and "- " bullet lists.
 * Never touches innerHTML, so there's no injection surface regardless of input.
 */
export function renderRichText(text: string, linkClassName?: string, listClassName?: string): ReactNode[] {
  return parseRichText(text).map((block, i) => {
    if (block.type === 'list') {
      return (
        <ul key={i} className={listClassName}>
          {block.items.map((item, j) => <li key={j}>{renderInline(item, linkClassName)}</li>)}
        </ul>
      );
    }
    return <span key={i}>{renderInline(block.inline, linkClassName)}</span>;
  });
}

/** Strips the markdown-lite syntax back to plain text, for one-line previews. */
export function stripRichTextSyntax(text: string): string {
  return text
    .replace(/^-\s+/, '')
    .replace(/\[([^\]]+)\]\((?:https?:\/\/[^\s)]+)\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1');
}

function inlineToPlainText(inline: RichTextInline[]): string {
  return inline.map((node) => (node.type === 'link' ? node.text : node.value)).join('');
}

/** Strips the markdown-lite syntax back to plain text across a full multi-line/list body, e.g. for clipboard export. */
export function richTextToPlainText(text: string): string {
  return parseRichText(text)
    .map((block) => block.type === 'list'
      ? block.items.map((item) => `- ${inlineToPlainText(item)}`).join('\n')
      : inlineToPlainText(block.inline))
    .join('\n');
}
